// Plain-language copy for capability and phone states. Server `detail` strings and
// reason codes are diagnostics: they go behind "Technical details", never in the main flow.
import type { Gate } from './capabilities.ts';
import { copyForCode } from './errorCopy.ts';

export interface Diagnostic {
  code: string | null;
  detail: string | null;
  requestId?: string | null;
}

export const SETUP_NOT_FINISHED = 'Your phone connection is not ready yet. An administrator needs to finish setup.';

export type Feature = 'calling' | 'outside_calls' | 'recording' | 'transcription';

/** One sentence for a gated feature; null when the feature is usable without caveat. */
export function featureNotice(feature: Feature, g: Gate): string | null {
  if (g.render === 'normal') return null;
  if (g.render === 'degraded') {
    switch (feature) {
      case 'calling':
        return 'Calling is having problems right now. Calls may not connect.';
      case 'outside_calls':
        return 'Calls to outside numbers are having problems right now.';
      case 'recording':
        return 'Recording is having problems right now. Your calls continue.';
      default:
        return 'Transcription is running behind. Your calls aren’t affected.';
    }
  }
  if (g.render === 'hidden') {
    if (g.reason === 'unknown') return 'Still checking what’s available in this workspace.';
    switch (feature) {
      case 'calling':
        return 'Your role doesn’t include making calls. Ask an admin if you need it.';
      case 'outside_calls':
        return 'Your role doesn’t include calling outside numbers.';
      case 'recording':
        return 'Your role doesn’t include call recordings.';
      default:
        return 'Your role doesn’t include transcripts.';
    }
  }
  switch (feature) {
    case 'calling':
      return g.reason === 'disabled' ? 'Calling is turned off for this workspace.' : g.reason === 'unimplemented' ? 'Calling isn’t available in this version.' : SETUP_NOT_FINISHED;
    case 'outside_calls':
      return 'Calls to outside numbers aren’t available yet. You can still call teammates.';
    case 'recording':
      return g.reason === 'disabled' ? 'Recording is turned off for this workspace.' : 'Recording isn’t set up for this workspace yet.';
    default:
      return 'Transcription isn’t available yet. Your calls aren’t affected.';
  }
}

/** Short status-bar value for a gate. */
export function gateShortLabel(g: Gate): string {
  if (g.render === 'normal') return 'Available';
  if (g.render === 'degraded') return 'Degraded';
  if (g.render === 'hidden') return g.reason === 'not_allowed' ? 'Not permitted' : 'Checking';
  return g.reason === 'disabled' ? 'Off' : g.reason === 'unimplemented' ? 'Not yet available' : 'Not set up';
}

export function gateDiagnostic(g: Gate, code: string | null): Diagnostic | null {
  if (g.render === 'normal' && !g.detail) return null;
  return { code, detail: g.detail };
}

/** Phone and device reason codes (server bootstrap, call engine, browser) in plain language. */
export function phoneReasonText(reason: string | null): string {
  switch (reason) {
    case null:
      return 'Your phone isn’t ready yet.';
    case 'sip_provisioning_unconfigured':
    case 'bootstrap_not_issued':
    case 'bootstrap_unconfigured':
    case 'bootstrap_invalid':
    case 'sip_hosts_not_configured':
    case 'telephony_unconfigured':
      return SETUP_NOT_FINISHED;
    case 'call_engine_not_integrated':
    case 'call_engine_unavailable':
      return 'This browser can’t connect call audio yet. An administrator needs to finish setup.';
    case 'bootstrap_unavailable':
    case 'bootstrap_expired':
      return 'Your phone connection expired. Reconnecting…';
    case 'bootstrap_revoked':
    case 'device_revoked':
      return 'This browser was removed as a phone. Set it up again to take calls here.';
    case 'microphone_denied':
      return 'Microphone blocked. Allow it in your browser’s site settings, then try again.';
    case 'microphone_permission_required':
      return 'Allow the microphone to use this browser as your phone.';
    case 'phone_active_in_another_tab':
    case 'phone_taken_by_another_tab':
      return 'Your phone is active in another tab.';
    case 'insecure_context':
      return 'Open this page over a secure (https) address to use the phone.';
    case 'webrtc_unsupported':
      return 'This browser can’t make calls. Use a current version of Chrome, Edge, Firefox or Safari.';
    case 'network_offline':
      return 'You’re offline. Your phone reconnects when you’re back online.';
    case 'retries_exhausted':
      return 'Couldn’t reach the phone system. Still trying.';
    case 'user_unregistered':
      return 'The phone is turned off on this browser.';
    case 'no_device':
      return 'Set up this browser as your phone to place and receive calls.';
    default:
      return 'Your phone is reconnecting.';
  }
}

/** Plain-language text for a call-engine error code. */
export function engineErrorText(code: string, serverCode?: string | null): string {
  if (serverCode === 'destination_blocked') return copyForCode(serverCode, 'This number is on the Do Not Call list.');
  if (serverCode === 'device_not_ready') return 'Your phone isn’t connected to the phone system yet. Try again in a moment.';
  // Every server refusal for an outside call has its own sentence (UX 8.2; lib/errorCopy.ts).
  if (serverCode?.startsWith('pstn_')) return copyForCode(serverCode, 'Calls to outside numbers aren’t available right now.');
  if (serverCode === 'telephony_unavailable') return 'The phone system is reconnecting. Please try again in a moment.';
  if (serverCode === 'telephony_unconfigured') return SETUP_NOT_FINISHED;
  switch (code) {
    case 'microphone_denied':
      return phoneReasonText('microphone_denied');
    case 'microphone_not_found':
      return 'No microphone found. Connect a headset, then try again.';
    case 'microphone_in_use':
      return 'Another app may be using your microphone.';
    case 'phone_not_ready':
      return 'Your phone isn’t ready yet.';
    case 'busy':
      return 'Finish your current call before starting another.';
    case 'pstn_not_enabled':
    case 'capability_unavailable':
      return 'That kind of call isn’t available yet.';
    case 'server_conflict':
      return 'The call changed. Review it, then try again.';
    case 'command_unconfirmed':
    case 'server_unreachable':
      return 'The phone system hasn’t confirmed yet. Checking…';
    case 'answer_failed':
      return 'The call couldn’t be answered on this browser.';
    case 'call_ended':
    case 'call_not_found':
      return 'That call has ended.';
    case 'output_device_unavailable':
      return 'That speaker couldn’t be used. Audio plays through your default output.';
    case 'input_device_unavailable':
      return 'That microphone couldn’t be used.';
    case 'output_selection_unsupported':
      return 'This browser plays audio through your system output only.';
    default:
      return 'The phone couldn’t do that. Try again.';
  }
}
